import { Link } from "react-router-dom";
import { SignupForm } from "wasp/client/auth";
import { AuthPageLayout } from "./AuthPageLayout";
import { useEffect, useRef, useState } from "react";
import { Image, Zap, BarChart3 } from "lucide-react";
import { analytics } from "../analytics";

// Mobile Showcase Component for improved visibility
const MobileImageShowcase = () => (
  <div className="my-6 px-2">
    <h3 className="text-lg font-semibold text-gray-900 mb-4 text-center">See our transformations</h3>
    <div className="space-y-6">
      {/* Bag Example */}
      <div>
        <h4 className="text-sm font-medium text-gray-700 mb-2">Premium Bag</h4>
        <div className="rounded-lg overflow-hidden shadow-md">
          <div className="relative" style={{ paddingBottom: '100%' }}>
            <img 
              src="/Bag-PRE.png" 
              alt="Professional bag showcase" 
              className="absolute inset-0 w-full h-full object-cover"
            />
          </div>
          <div className="p-3 bg-white">
            <p className="text-sm font-medium text-gray-900">Basic Product Photo</p>
            <p className="text-xs text-gray-500">Standard product image</p>
          </div>
        </div>
        <div className="rounded-lg overflow-hidden shadow-md mt-4">
          <div className="relative" style={{ paddingBottom: '110%' }}>
            <img 
              src="/Bag-POST.jpeg" 
              alt="Lifestyle product showcase" 
              className="absolute inset-0 w-full h-full object-cover"
            />
          </div>
          <div className="p-3 bg-white">
            <p className="text-sm font-medium text-gray-900">Lifestyle presentation</p>
            <p className="text-xs text-gray-500">Contextual styling by AI</p>
          </div>
        </div>
      </div>
      
      {/* Table Example */}
      <div className="mt-8">
        <h4 className="text-sm font-medium text-gray-700 mb-2">Elegant Table</h4>
        <div className="rounded-lg overflow-hidden shadow-md">
          <div className="relative" style={{ paddingBottom: '75%' }}>
            <img 
              src="/table-pre.jpeg" 
              alt="Original table product photo" 
              className="absolute inset-0 w-full h-full object-cover"
            />
          </div>
          <div className="p-3 bg-white">
            <p className="text-sm font-medium text-gray-900">Basic product photo</p>
            <p className="text-xs text-gray-500">Standard product image</p>
          </div>
        </div>
        <div className="rounded-lg overflow-hidden shadow-md mt-4">
          <div className="relative" style={{ paddingBottom: '75%' }}>
            <img 
              src="/table-post.jpeg" 
              alt="Enhanced table in room setting" 
              className="absolute inset-0 w-full h-full object-cover"
            />
          </div>
          <div className="p-3 bg-white">
            <p className="text-sm font-medium text-gray-900">Room setting visualization</p>
            <p className="text-xs text-gray-500">AI-enhanced lifestyle context</p>
          </div>
        </div>
      </div>
      
      {/* Honey Example */}
      <div className="mt-8">
        <h4 className="text-sm font-medium text-gray-700 mb-2">Organic Honey</h4>
        <div className="rounded-lg overflow-hidden shadow-md">
          <div className="relative" style={{ paddingBottom: '120%' }}>
            <img 
              src="/honey-PRE.png" 
              alt="Basic honey product photo" 
              className="absolute inset-0 w-full h-full object-cover"
            />
          </div>
          <div className="p-3 bg-white">
            <p className="text-sm font-medium text-gray-900">Basic product shot</p>
            <p className="text-xs text-gray-500">Manufacturer's product image</p>
          </div>
        </div>
        <div className="rounded-lg overflow-hidden shadow-md mt-4">
          <div className="relative" style={{ paddingBottom: '120%' }}>
            <img 
              src="/honey-POST.jpg" 
              alt="Enhanced honey lifestyle photo" 
              className="absolute inset-0 w-full h-full object-cover"
            />
          </div>
          <div className="p-3 bg-white">
            <p className="text-sm font-medium text-gray-900">Lifestyle presentation</p>
            <p className="text-xs text-gray-500">Enhanced marketing imagery</p>
          </div>
        </div>
      </div>
    </div>
  </div>
);

// Desktop Showcase Component
const DesktopFullWidthShowcase = () => (
  <div className="max-w-5xl mx-auto">
    {/* Header section */}
    <div className="text-center mb-10">
      <h2 className="text-3xl font-bold text-gray-900 mb-3">Transform Your Product Images</h2>
      <p className="text-lg text-gray-600 max-w-3xl mx-auto">
        See how our AI technology enhances ordinary product photos into compelling marketing assets
      </p>
    </div>
    
    {/* Products Grid */}
    <div className="grid grid-cols-3 gap-8 mb-12">
      {/* Bag Transformation */}
      <div className="bg-white rounded-xl shadow-md overflow-hidden transform transition hover:shadow-lg">
        <div className="p-3 border-b border-gray-100">
          <h3 className="font-semibold text-gray-900">Premium Bag</h3>
        </div>
        <div className="p-4">
          <div className="relative rounded-lg overflow-hidden mb-4" style={{ paddingBottom: '100%' }}>
            <img 
              src="/Bag-PRE.png" 
              alt="Original bag photo"
              className="absolute inset-0 w-full h-full object-cover"
            />
            <div className="absolute bottom-2 left-2 bg-white/80 backdrop-blur-sm text-xs px-2 py-1 rounded">Before</div>
          </div>
          <div className="relative rounded-lg overflow-hidden" style={{ paddingBottom: '110%' }}>
            <img 
              src="/Bag-POST.jpeg" 
              alt="Enhanced bag in lifestyle context"
              className="absolute inset-0 w-full h-full object-cover"
            />
            <div className="absolute bottom-2 left-2 bg-purple-600/80 backdrop-blur-sm text-white text-xs px-2 py-1 rounded">After</div>
          </div>
        </div>
      </div>
      
      {/* Table Transformation */}
      <div className="bg-white rounded-xl shadow-md overflow-hidden transform transition hover:shadow-lg">
        <div className="p-3 border-b border-gray-100">
          <h3 className="font-semibold text-gray-900">Elegant Table</h3>
        </div>
        <div className="p-4">
          <div className="relative rounded-lg overflow-hidden mb-4" style={{ paddingBottom: '75%' }}>
            <img 
              src="/table-pre.jpeg" 
              alt="Original table photo"
              className="absolute inset-0 w-full h-full object-cover"
            />
            <div className="absolute bottom-2 left-2 bg-white/80 backdrop-blur-sm text-xs px-2 py-1 rounded">Before</div>
          </div>
          <div className="relative rounded-lg overflow-hidden" style={{ paddingBottom: '75%' }}>
            <img 
              src="/table-post.jpeg" 
              alt="Enhanced table in room setting"
              className="absolute inset-0 w-full h-full object-cover"
            />
            <div className="absolute bottom-2 left-2 bg-purple-600/80 backdrop-blur-sm text-white text-xs px-2 py-1 rounded">After</div>
          </div>
        </div>
      </div>
      
      {/* Honey Transformation */}
      <div className="bg-white rounded-xl shadow-md overflow-hidden transform transition hover:shadow-lg">
        <div className="p-3 border-b border-gray-100">
          <h3 className="font-semibold text-gray-900">Organic Honey</h3>
        </div>
        <div className="p-4">
          <div className="relative rounded-lg overflow-hidden mb-4" style={{ paddingBottom: '100%' }}>
            <img 
              src="/honey-PRE.png" 
              alt="Original honey product photo"
              className="absolute inset-0 w-full h-full object-cover"
            />
            <div className="absolute bottom-2 left-2 bg-white/80 backdrop-blur-sm text-xs px-2 py-1 rounded">Before</div>
          </div>
          <div className="relative rounded-lg overflow-hidden" style={{ paddingBottom: '100%' }}>
            <img 
              src="/honey-POST.jpg" 
              alt="Enhanced honey lifestyle photo"
              className="absolute inset-0 w-full h-full object-cover"
            />
            <div className="absolute bottom-2 left-2 bg-purple-600/80 backdrop-blur-sm text-white text-xs px-2 py-1 rounded">After</div>
          </div>
        </div>
      </div>
    </div>
    
    {/* Stats Section */}
    <div className="grid grid-cols-3 gap-8 mb-12">
      <div className="bg-white/90 backdrop-blur-sm p-6 rounded-xl shadow-md">
        <div className="text-3xl font-bold text-purple-600 mb-2">+32%</div>
        <div className="text-gray-700">Average conversion growth reported by our clients</div>
      </div>
      
      <div className="bg-white/90 backdrop-blur-sm p-6 rounded-xl shadow-md">
        <div className="text-3xl font-bold text-purple-600 mb-2">+76%</div>
        <div className="text-gray-700">Quality perception improvement in consumer testing</div>
      </div>
      
      <div className="bg-white/90 backdrop-blur-sm p-6 rounded-xl shadow-md">
        <div className="text-3xl font-bold text-purple-600 mb-2">85%</div>
        <div className="text-gray-700">Of customers report increased viewer engagement</div>
      </div>
    </div>
    
    {/* Testimonial Section */}
    <div className="bg-white/90 backdrop-blur-sm p-8 rounded-xl shadow-md mb-20">
      <div className="max-w-3xl mx-auto">
        <p className="text-lg italic text-gray-700 mb-8">
          "I was skeptical at first, but MarketplaceIQ proved me wrong within the first week. Their AI writes titles and descriptions that actually grab attention - just like an expert copywriter. Our conversion rates shot up, and I spend way less time fixing product data now."
        </p>
        <div className="flex items-center gap-4">
          <img
            src="https://www.marketplaceiq.io/wp-content/uploads/2024/11/yasemin.webp"
            alt={"Yasemin Gülle"}
            className="w-14 h-14 rounded-full object-cover"
          />
          <div>
            <p className="font-semibold text-gray-900">{"Yasemin Gülle"}</p>
            <p className="text-sm text-gray-600">
              {"Sr. Digital Channels Manager"}
            </p>
          </div>
        </div>
      </div>
    </div>
  </div>
);

const FeatureCard = ({ icon: Icon, title, description }: { 
  icon: any, 
  title: string, 
  description: string 
}) => (
  <div className="flex items-start gap-3 p-4 bg-white/80 backdrop-blur-sm rounded-lg shadow-sm">
    <Icon className="w-5 h-5 text-purple-500 mt-1 shrink-0" />
    <div>
      <h3 className="font-semibold text-gray-900">{title}</h3>
      <p className="text-sm text-gray-600">{description}</p>
    </div>
  </div>
);

const ValueProps = () => {
  return (
    <div className="flex flex-col gap-8 pr-0 md:pr-8">
      <div className="space-y-3">
        <h2 className="text-3xl font-bold text-gray-900">
          {"Transform Your Product Images"}
        </h2>
        <p className="text-lg text-gray-600">
          {"Join 2,500+ merchants using AI-generated images that convert"}
        </p>
      </div>

      <div className="space-y-4">
        <FeatureCard
          icon={Image}
          title={"AI Image Generation"}
          description={"Create professional product images from simple descriptions"}
        />
        <FeatureCard
          icon={Zap}
          title={"Instant Conversion Boost"}
          description={"AI-optimized visuals that increase click-through rates"}
        />
        <FeatureCard
          icon={BarChart3}
          title={"Image Performance Analytics"}
          description={"Track which images drive the most sales and engagement"}
        />
      </div>

      <div className="p-6 bg-white/80 backdrop-blur-sm rounded-lg">
        {/* Metrics */}
        <div className="grid grid-cols-2 gap-6 mb-6">
          <div>
            <div className="text-2xl font-bold text-purple-600">+32%</div>
            <div className="text-sm text-gray-600">{"conversion growth"}</div>
          </div>
          <div>
            <div className="text-2xl font-bold text-purple-600">+76%</div>
            <div className="text-sm text-gray-600">{"quality gain"}</div>
          </div>
        </div>

        {/* Testimonial */}
        <p className="text-gray-700 text-sm italic mb-6">
          {
            "I was skeptical at first, but MarketplaceIQ proved me wrong within the first week. Their AI writes titles and descriptions that actually grab attention - just like an expert copywriter. Our conversion rates shot up, and I spend way less time fixing product data now."
          }
        </p>

        {/* Author */}
        <div className="flex items-center gap-4">
          <img
            src="https://www.marketplaceiq.io/wp-content/uploads/2024/11/yasemin.webp"
            alt={"Yasemin Gülle"}
            className="w-12 h-12 rounded-full object-cover"
          />
          <div>
            <p className="font-semibold text-gray-900">{"Yasemin Gülle"}</p>
            <p className="text-sm text-gray-600">
              {"Sr. Digital Channels Manager"}
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export function Signup() {
  const signupFormRef = useRef<HTMLDivElement>(null);
  const [isMobile, setIsMobile] = useState(false);
  
  useEffect(() => {
    // Check if mobile view
    const checkIfMobile = () => {
      setIsMobile(window.innerWidth < 768);
    };
    
    // Initial check
    checkIfMobile();
    
    // Add event listener for resize
    window.addEventListener('resize', checkIfMobile);
    
    // Track the signup event
    setTimeout(() => {
      const referralSource = localStorage.getItem("register_rs");
      analytics.sendSignupStartedEvent({ referralSource : referralSource ?? "direct"});
    }, 1000);

    // Hide WASP email/password fields and only show Google button
    setTimeout(() => {
      if (signupFormRef.current) {
        // Hide email/password form
        const formElements = signupFormRef.current.querySelectorAll('form');
        const orContinueWith = signupFormRef.current.querySelector('div[class^="OrContinueWith"]');

        if (formElements.length > 0) {
          formElements.forEach(form => {
            (form as HTMLElement).style.display = 'none';
          });
        }

        if (orContinueWith && orContinueWith instanceof HTMLElement) {
          orContinueWith.style.display = 'none';
        }
      }
    }, 100);
    
    // Cleanup event listener
    return () => {
      window.removeEventListener('resize', checkIfMobile);
    };
  }, []);

  // Function to click the Google Sign-In button programmatically
  const handleCustomGoogleSignup = () => {
    if (signupFormRef.current) {
      // Temporarily re-enable pointer events
      signupFormRef.current.style.pointerEvents = 'auto';
      
      const googleButton = signupFormRef.current.querySelector('a[href*="/auth/google/login"]');
      if (googleButton && googleButton instanceof HTMLElement) {
        googleButton.click();
      }
      
      // Disable pointer events again (though it doesn't matter after redirect)
      setTimeout(() => {
        if (signupFormRef.current) {
          signupFormRef.current.style.pointerEvents = 'none';
        }
      }, 100);
    }
  };

  return (
    <>
      {/* Custom full-width layout for desktop */}
      {!isMobile && (
        <div className="min-h-screen bg-gradient-to-br from-purple-50 via-blue-50 to-green-50 overflow-hidden relative">
          <div className="pt-8 pb-24">
            {/* Logo */}
            <div className="flex justify-center mb-10">
              <img
                src="https://www.marketplaceiq.io/wp-content/uploads/2024/12/black2.png"
                className="h-8 w-auto"
              />
            </div>
            
            {/* Main content */}
            <DesktopFullWidthShowcase />
            
            {/* Floating Sign Up Bar */}
            <div className="fixed bottom-0 left-0 right-0 bg-white shadow-[0_-4px_10px_rgba(0,0,0,0.07)] border-t border-gray-100 py-4 z-50">
              <div className="max-w-5xl mx-auto px-8 flex items-center justify-between">
                <div>
                  <h3 className="text-lg font-semibold text-gray-900">Ready to transform your product images?</h3>
                  <p className="text-sm text-gray-600">Join thousands of successful merchants today</p>
                </div>
                <div className="flex items-center gap-6">
                  <div className="text-sm font-medium">
                    <span className="text-gray-600">Already have an account?</span>{" "}
                    <Link to="/login" className="text-purple-600 hover:text-purple-700">
                      Go to login
                    </Link>
                  </div>
                  <button 
                    className="bg-purple-600 hover:bg-purple-700 text-white font-medium py-3 px-6 rounded-lg transition-colors flex items-center justify-center gap-2"
                    onClick={handleCustomGoogleSignup}
                  >
                    <svg className="w-5 h-5" viewBox="0 0 24 24">
                      <g id="brand" stroke="none" strokeWidth="1" fill="none" fillRule="evenodd">
                        <g id="google" fill="#FFFFFF" fillRule="nonzero">
                          <path
                            d="M11.99,13.9 L11.99,10.18 L21.35,10.18 C21.49,10.81 21.6,11.4 21.6,12.23 C21.6,17.94 17.77,22 12,22 C6.48,22 2,17.52 2,12 C2,6.48 6.48,2 12,2 C14.7,2 16.96,2.99 18.69,4.61 L15.85,7.37 C15.13,6.69 13.87,5.89 12,5.89 C8.69,5.89 5.99,8.64 5.99,12.01 C5.99,15.38 8.69,18.13 12,18.13 C15.83,18.13 17.24,15.48 17.5,13.91 L11.99,13.91 L11.99,13.9 Z"
                            id="Shape"
                          ></path>
                        </g>
                      </g>
                    </svg>
                    Sign up with Google
                  </button>
                </div>
              </div>
            </div>
          </div>
          
          {/* Hidden original WASP signup form - we need this for the Google button */}
          <div ref={signupFormRef} style={{ position: 'absolute', opacity: 0, pointerEvents: 'none', height: 0, overflow: 'hidden' }}>
            <SignupForm />
          </div>
          
          {/* Footer terms */}
          <div className="text-center text-xs text-gray-500 fixed bottom-0 left-0 right-0 pb-1 z-40">
            By signing up, you agree to our{" "}
            <a href="https://www.marketplaceiq.io/home/terms-of-use/" className="text-purple-600 hover:underline">terms</a>
            {" "}and{" "}
            <a href="https://www.marketplaceiq.io/privacy-policy/" className="text-purple-600 hover:underline">privacy policy</a>
          </div>
        </div>
      )}
      
      {/* Use existing AuthPageLayout for mobile */}
      {isMobile && (
        <AuthPageLayout
          twoColumn={true}
          valueProps={<ValueProps />}
        >
          <>
            {/* Mobile showcase - only visible on small screens */}
            <div className="md:hidden mb-8">
              <MobileImageShowcase />
            </div>
            
            {/* Sign up content */}
            <div className="space-y-6">
              {/* Simplified header */}
              <h1 className="text-xl md:text-2xl font-bold text-gray-900 text-center md:text-left">
                {isMobile ? "Join to transform your images" : "Get started with MarketplaceIQ"}
              </h1>
              
              {/* Login link */}
              <div className="text-sm font-medium text-gray-900 text-center">
                I already have an account (
                <Link to="/login" className="text-purple-600 hover:text-purple-700">
                  go to login
                </Link>
                ).
              </div>
            </div>
            
            {/* Mobile floating CTA */}
            {isMobile && (
              <div className="fixed bottom-0 left-0 right-0 z-50">
                {/* Visual separator gradient */}
                <div 
                  className="h-8 w-full" 
                  style={{ 
                    background: 'linear-gradient(to bottom, rgba(255,255,255,0) 0%, rgba(255,255,255,0.9) 60%, rgba(255,255,255,1) 100%)',
                    marginBottom: '-1px'
                  }}
                ></div>
                
                {/* Button container with improved styling */}
                <div className="bg-white p-4 shadow-[0_-4px_10px_rgba(0,0,0,0.07)] border-t border-gray-100">
                  <button 
                    className="w-full bg-purple-600 hover:bg-purple-700 text-white font-semibold py-4 px-4 rounded-lg transition-colors flex items-center justify-center gap-2 shadow-sm"
                    onClick={handleCustomGoogleSignup}
                  >
                    <svg className="w-5 h-5" viewBox="0 0 24 24">
                      <g id="brand" stroke="none" strokeWidth="1" fill="none" fillRule="evenodd">
                        <g id="google" fill="#FFFFFF" fillRule="nonzero">
                          <path
                            d="M11.99,13.9 L11.99,10.18 L21.35,10.18 C21.49,10.81 21.6,11.4 21.6,12.23 C21.6,17.94 17.77,22 12,22 C6.48,22 2,17.52 2,12 C2,6.48 6.48,2 12,2 C14.7,2 16.96,2.99 18.69,4.61 L15.85,7.37 C15.13,6.69 13.87,5.89 12,5.89 C8.69,5.89 5.99,8.64 5.99,12.01 C5.99,15.38 8.69,18.13 12,18.13 C15.83,18.13 17.24,15.48 17.5,13.91 L11.99,13.91 L11.99,13.9 Z"
                            id="Shape"
                          ></path>
                        </g>
                      </g>
                    </svg>
                    Sign up with Google
                  </button>
                </div>
              </div>
            )}
            
            {/* Add bottom padding to avoid content being hidden behind the fixed CTA on mobile */}
            {isMobile && <div className="pb-24"></div>}
            
            {/* Hidden original WASP signup form - we need this for the Google button */}
            <div ref={signupFormRef} style={{ position: 'absolute', opacity: 0, pointerEvents: 'none', height: 0, overflow: 'hidden' }}>
              <SignupForm />
            </div>
          </>
        </AuthPageLayout>
      )}
    </>
  );
}